import React, { useState, useEffect } from "react";
import { API } from "../api";
import "../styles/style_profil.css";
import "../styles/style_addressManager.css";

export default function AddressManager({ user }) {
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressMessage, setAddressMessage] = useState({ type: "", text: "" });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState({
    country: "",
    postal_code: "",
    city: "",
    street_name: "",
    complement: "",
    is_default: false,
  });

  useEffect(() => {
    if (user) fetchAddresses();
  }, [user]);

  const fetchAddresses = async () => {
    if (!user) return;
    setAddressesLoading(true);
    setAddressMessage({ type: "", text: "" });
    try {
      const res = await API.get("user_addresses", { user_id: user.id });
      setAddresses(Array.isArray(res) ? res : []);
    } catch (err) {
      setAddressMessage({ type: "error", text: err.message || "Impossible de récupérer les adresses" });
    } finally {
      setAddressesLoading(false);
    }
  };

  const handleAddressInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setAddressMessage({ type: "", text: "" });
  };

  const resetAddressForm = () => {
    setAddressForm({ country: "", postal_code: "", city: "", street_name: "", complement: "", is_default: false });
    setEditingAddressId(null);
    setIsCreatingNew(false);
  };

  const validateAddress = () => {
    const required = ["country", "postal_code", "city", "street_name"];
    for (const key of required) if (!addressForm[key]) return `Veuillez renseigner ${key.replace("_", " ")}`;
    return null;
  };

  const handleSubmitAddress = async (e) => {
    e.preventDefault();
    setAddressMessage({ type: "", text: "" });

    const validationError = validateAddress();
    if (validationError) return setAddressMessage({ type: "error", text: validationError });

    if (addresses.length >= 10) {
      setAddressMessage({ type: "error", text: "Vous avez atteint la limite de 10 adresses." });
      return;
    }

    try {
      await API.post(
        "user_addresses",
        {
          user_id: user.id,
          country: addressForm.country,
          postal_code: addressForm.postal_code,
          city: addressForm.city,
          street_name: addressForm.street_name,
          complement: addressForm.complement,
          is_default: addressForm.is_default,
        },
        {}
      );

      setAddressMessage({ type: "success", text: "Adresse ajoutée" });
      resetAddressForm();
      fetchAddresses();
    } catch (err) {
      setAddressMessage({ type: "error", text: err.message || "Erreur lors de l'enregistrement" });
    }
  };

  const handleUpdateAddress = async (e, addr) => {
    e.preventDefault();
    setAddressMessage({ type: "", text: "" });

    const formData = new FormData(e.target);
    const updatedData = {
      country: formData.get("country"),
      postal_code: formData.get("postal_code"),
      city: formData.get("city"),
      street_name: formData.get("street_name"),
      complement: formData.get("complement"),
      is_default: formData.get("is_default") === "on",
    };

    const required = ["country", "postal_code", "city", "street_name"];
    for (const key of required) {
      if (!updatedData[key]) {
        setAddressMessage({ type: "error", text: `Veuillez renseigner ${key.replace("_", " ")}` });
        return;
      }
    }

    try {
      await API.put("user_addresses", { id: addr.id }, updatedData);
      setAddressMessage({ type: "success", text: "Adresse mise à jour" });
      setEditingAddressId(null);
      fetchAddresses();
    } catch (err) {
      setAddressMessage({ type: "error", text: err.message || "Erreur lors de la mise à jour" });
    }
  };

  const handleEditAddress = (address) => {
    if (editingAddressId === address.id) {
      setEditingAddressId(null);
    } else {
      setIsCreatingNew(false);
      setEditingAddressId(address.id);
      setAddressMessage({ type: "", text: "" });
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await API.put("user_addresses", { id }, { is_default: true });
      fetchAddresses();
    } catch (err) {
      setAddressMessage({ type: "error", text: err.message || "Impossible de définir l'adresse par défaut" });
    }
  };

  const handleDeleteAddress = async (id) => {
    if (deletingAddressId === id) {
      // Confirm and delete
      try {
        await API.delete("user_addresses", { id });
        if (editingAddressId === id) resetAddressForm();
        setDeletingAddressId(null);
        setAddressMessage({ type: "success", text: "Adresse supprimée" });
        fetchAddresses();
      } catch (err) {
        setAddressMessage({ type: "error", text: err.message || "Impossible de supprimer l'adresse" });
      }
    } else {
      // Show confirmation
      setDeletingAddressId(id);
    }
  };

  return (
    <div className="profil-section">
      <div className="profil-section-header">
        <h2>Adresses</h2>
        <span className="profil-section-icon">🏠</span>
      </div>

      {addressMessage.text && (
        <div className={`profil-message ${addressMessage.type}`}>
          <span>{addressMessage.text}</span>
        </div>
      )}

      {!isCreatingNew && (
        <button
          type="button"
          className="profil-button profil-button-primary"
          disabled={addressesLoading || addresses.length >= 10}
          onClick={() => {
            setIsCreatingNew(true);
            setEditingAddressId(null);
            setAddressMessage({ type: "", text: "" });
          }}
        >
          Ajouter une nouvelle adresse
        </button>
      )}

      {isCreatingNew && (
        <div className="profil-address-card profil-address-card-expanded profil-address-card-new">
          <div className="profil-address-main">
            <div>
              <strong>Nouvelle adresse</strong>
            </div>
            <span className="profil-badge profil-badge-new">En cours</span>
          </div>

          <div className="profil-address-actions">
            <button type="button" className="profil-button" onClick={resetAddressForm}>
              Annuler
            </button>
          </div>

          <form className="profil-address-edit-form" onSubmit={handleSubmitAddress}>
            <div className="profil-form-row">
              <div className="profil-form-group">
                <label htmlFor="country">Pays</label>
                <input id="country" name="country" value={addressForm.country} onChange={handleAddressInputChange} placeholder="FR" />
              </div>
              <div className="profil-form-group">
                <label htmlFor="postal_code">Code postal</label>
                <input id="postal_code" name="postal_code" value={addressForm.postal_code} onChange={handleAddressInputChange} placeholder="75001" />
              </div>
            </div>

            <div className="profil-form-group">
              <label htmlFor="city">Ville</label>
              <input id="city" name="city" value={addressForm.city} onChange={handleAddressInputChange} placeholder="Paris" />
            </div>

            <div className="profil-form-group">
              <label htmlFor="street_name">Adresse</label>
              <input id="street_name" name="street_name" value={addressForm.street_name} onChange={handleAddressInputChange} placeholder="10 rue de Rivoli" />
            </div>

            <div className="profil-form-group">
              <label htmlFor="complement">Complément</label>
              <input id="complement" name="complement" value={addressForm.complement} onChange={handleAddressInputChange} placeholder="Bâtiment B" />
            </div>

            <div className="profil-form-group profil-form-checkbox">
              <input type="checkbox" id="is_default" name="is_default" checked={addressForm.is_default} onChange={handleAddressInputChange} />
              <label htmlFor="is_default">Définir comme adresse par défaut</label>
            </div>

            <button type="submit" className="profil-button profil-button-primary">
              Enregistrer l'adresse
            </button>
          </form>
        </div>
      )}

      <div className="profil-address-list">
        {addressesLoading ? (
          <div className="profil-loading">Chargement des adresses...</div>
        ) : addresses.length === 0 ? (
          <p>Aucune adresse enregistrée.</p>
        ) : (
          addresses.map((addr) => (
            <div key={addr.id} className={`profil-address-card ${editingAddressId === addr.id ? "profil-address-card-expanded" : ""}`}>
              <div className="profil-address-main">
                <div>
                  <strong>{addr.street_name}</strong>
                  <div>{addr.complement}</div>
                  <div>
                    {addr.postal_code} {addr.city} ({addr.country})
                  </div>
                </div>
                {addr.is_default && <span className="profil-badge">Par défaut</span>}
              </div>

              <div className="profil-address-actions">
                <button type="button" className="profil-button" onClick={() => handleEditAddress(addr)}>
                  {editingAddressId === addr.id ? "Annuler" : "Modifier"}
                </button>
                {!addr.is_default && (
                  <button type="button" className="profil-button profil-button-secondary" onClick={() => handleSetDefault(addr.id)}>
                    Définir par défaut
                  </button>
                )}
                <button type="button" className={`profil-button ${deletingAddressId === addr.id ? "profil-button-danger" : ""}`} onClick={() => handleDeleteAddress(addr.id)}>
                  {deletingAddressId === addr.id ? "✓ Confirmer" : "Supprimer"}
                </button>
                {deletingAddressId === addr.id && (
                  <button type="button" className="profil-button" onClick={() => setDeletingAddressId(null)}>
                    ✕ Annuler
                  </button>
                )}
              </div>

              {editingAddressId === addr.id && (
                <form className="profil-address-edit-form" onSubmit={(e) => handleUpdateAddress(e, addr)}>
                  <div className="profil-form-row">
                    <div className="profil-form-group">
                      <label htmlFor={`country-${addr.id}`}>Pays</label>
                      <input id={`country-${addr.id}`} name="country" defaultValue={addr.country} placeholder="FR" />
                    </div>
                    <div className="profil-form-group">
                      <label htmlFor={`postal_code-${addr.id}`}>Code postal</label>
                      <input id={`postal_code-${addr.id}`} name="postal_code" defaultValue={addr.postal_code} placeholder="75001" />
                    </div>
                  </div>

                  <div className="profil-form-group">
                    <label htmlFor={`city-${addr.id}`}>Ville</label>
                    <input id={`city-${addr.id}`} name="city" defaultValue={addr.city} placeholder="Paris" />
                  </div>

                  <div className="profil-form-group">
                    <label htmlFor={`street_name-${addr.id}`}>Adresse</label>
                    <input id={`street_name-${addr.id}`} name="street_name" defaultValue={addr.street_name} placeholder="10 rue de Rivoli" />
                  </div>

                  <div className="profil-form-group">
                    <label htmlFor={`complement-${addr.id}`}>Complément</label>
                    <input id={`complement-${addr.id}`} name="complement" defaultValue={addr.complement} placeholder="Bâtiment B" />
                  </div>

                  <div className="profil-form-group profil-form-checkbox">
                    <input type="checkbox" id={`is_default-${addr.id}`} name="is_default" defaultChecked={addr.is_default} />
                    <label htmlFor={`is_default-${addr.id}`}>Définir comme adresse par défaut</label>
                  </div>

                  <button type="submit" className="profil-button profil-button-primary">
                    Enregistrer les modifications
                  </button>
                </form>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}